// ═══════════════════════════════════════════════════════════════════════════
// @dxf-ai-editor/command-registry — Zod descriptor schemas
// ═══════════════════════════════════════════════════════════════════════════
//
// Zod-level mirror of the TypeScript types in `./types.ts`. Used as
// defense-in-depth by the validator script (`scripts/validate-command-registry.mts`):
// every descriptor authored in `commands/*.ts` must pass `commandDescriptorSchema.parse`.
// This catches typos, missing localised fields and accidental shape drift
// that the TypeScript compiler alone cannot detect (e.g. mistyped enum
// strings inside a deeply nested object literal).
//
// IMPORTANT: keep these enums in lock-step with the unions in `./types.ts`.
// The `satisfies` checks at the bottom of the file enforce that mapping at
// compile time.
// ═══════════════════════════════════════════════════════════════════════════

import { z } from "zod";
import type {
  CommandCategory,
  CommandDescriptor,
  CommandStatus,
  DraftStepDescriptor,
  DraftStepExpects,
  LocalizedHelp,
  ParameterDescriptor,
  ParameterType,
  RibbonTab,
} from "./types.js";

// ── Primitive enums ────────────────────────────────────────────────────────

export const commandStatusSchema = z.enum([
  "implemented",
  "stub",
  "deprecated",
]);

export const commandCategorySchema = z.enum([
  "drawing",
  "modify",
  "dimension",
  "annotation",
  "block",
  "collab",
  "attribute",
  "array",
  "layout",
  "dynamic",
  "reference",
  "system",
]);

export const parameterTypeSchema = z.enum([
  "point2d",
  "point3d",
  "number",
  "string",
  "enum",
  "entityId",
  "entityIdList",
  "boolean",
  "object",
]);

export const draftStepExpectsSchema = z.enum([
  "point",
  "distance",
  "angle",
  "selection",
  "text",
  "keyword",
  "dialog",
]);

export const ribbonTabSchema = z.enum([
  "draw",
  "annotate",
  "modify",
  "layout",
  "propertyflows",
]);

// ── Composite shapes ───────────────────────────────────────────────────────

// `.readonly()` mirrors `readonly` in the TS type counterparts in `./types.ts`.
// Without it, the compile-time parity assertions at the bottom of this file
// fail with `readonly string[] is not assignable to mutable string[]`.
// `.readonly()` is purely a type-level marker — it does not change runtime
// validation behaviour (Zod still parses arrays as plain JS arrays).
export const localizedHelpSchema = z.object({
  short: z.string(),
  long: z.string(),
  examples: z.array(z.string()).readonly(),
  seeAlso: z.array(z.string()).readonly(),
});

export const parameterDescriptorSchema = z.object({
  name: z.string().min(1),
  type: parameterTypeSchema,
  enumValues: z.array(z.string()).readonly().optional(),
  required: z.boolean(),
  defaultValue: z.string().optional(),
  description: z.object({
    ru: z.string(),
    en: z.string(),
  }),
});

export const draftStepDescriptorSchema = z.object({
  stepId: z.string().min(1),
  promptKeyRu: z.string().min(1),
  promptKeyEn: z.string().min(1),
  expects: draftStepExpectsSchema,
  keywords: z.array(z.string()).readonly().optional(),
});

export const commandDescriptorSchema = z.object({
  id: z.string().min(1),
  category: commandCategorySchema,
  status: commandStatusSchema,
  // AI-callable contract flag (ТЗ §8 / §10.1). Required — Zod rejects any
  // descriptor that omits it, which is how the registry enforces "every
  // command declares its AI-callability" at validate-time.
  aiCallable: z.boolean(),
  aiSchemaRef: z.string().min(1).optional(),
  aliases: z.object({
    en: z.array(z.string()).readonly(),
    ru: z.array(z.string()).readonly(),
  }),
  commandTypeLiterals: z.array(z.string()).readonly(),
  draftCommandId: z.string().min(1).optional(),
  draftSteps: z.array(draftStepDescriptorSchema).readonly().optional(),
  parameters: z.array(parameterDescriptorSchema).readonly(),
  help: z.object({
    ru: localizedHelpSchema,
    en: localizedHelpSchema,
  }),
  uiMetadata: z.object({
    labelKeyRu: z.string().min(1),
    labelKeyEn: z.string().min(1),
    ribbonTab: ribbonTabSchema.nullable(),
    ribbonSection: z.string().nullable(),
    glyph: z.string().nullable(),
  }),
  reducerLocations: z
    .array(
      z.object({
        commandTypeLiteral: z.string().min(1),
        file: z.string().min(1),
      }),
    )
    .readonly(),
});

// ── Compile-time parity checks ─────────────────────────────────────────────
//
// Each enum below is asserted to equal its TypeScript-union counterpart.
// If the TS union grows but the Zod enum does not (or vice-versa), the
// `satisfies` checks fail at typecheck time, which is the earliest point
// the drift can be caught without running the validator.

const _statusParity = [
  "implemented",
  "stub",
  "deprecated",
] as const satisfies readonly CommandStatus[];

const _categoryParity = [
  "drawing",
  "modify",
  "dimension",
  "annotation",
  "block",
  "collab",
  "attribute",
  "array",
  "layout",
  "dynamic",
  "reference",
  "system",
] as const satisfies readonly CommandCategory[];

const _parameterTypeParity = [
  "point2d",
  "point3d",
  "number",
  "string",
  "enum",
  "entityId",
  "entityIdList",
  "boolean",
  "object",
] as const satisfies readonly ParameterType[];

const _expectsParity = [
  "point",
  "distance",
  "angle",
  "selection",
  "text",
  "keyword",
  "dialog",
] as const satisfies readonly DraftStepExpects[];

const _ribbonParity = [
  "draw",
  "annotate",
  "modify",
  "layout",
  "propertyflows",
] as const satisfies readonly RibbonTab[];

// Burn the unused-symbol warning — these consts exist only for the
// `satisfies` side-effect.
void _statusParity;
void _categoryParity;
void _parameterTypeParity;
void _expectsParity;
void _ribbonParity;

// Inferred Zod types are also checked against the TS shapes. The direction
// matters under `exactOptionalPropertyTypes: true`:
//
//   • Zod's `.optional()` produces `T | undefined` — i.e. the property MAY
//     be present-and-undefined.
//   • Our TS `enumValues?: readonly string[]` (without `| undefined`)
//     under exact-optional means the property MAY be omitted but if present
//     MUST be a `readonly string[]`, not undefined.
//
// These two are not bidirectionally assignable. The canonical source of
// truth is the TS type (per the file header), so we check the direction
// that matters in practice: any value built from the TS type must satisfy
// the Zod-inferred shape. This catches both shape drift (missing fields)
// and type drift (wrong primitive type) at compile time, while remaining
// compatible with `exactOptionalPropertyTypes`.
//
// Runtime parity in the OTHER direction (Zod parse output → TS) is
// guaranteed by `commandDescriptorSchema.safeParse(d)` calls in the
// validator script (`scripts/validate-command-registry.mts`, check A.SHAPE).
type _LocalizedHelpZ = z.infer<typeof localizedHelpSchema>;
type _ParameterDescriptorZ = z.infer<typeof parameterDescriptorSchema>;
type _DraftStepDescriptorZ = z.infer<typeof draftStepDescriptorSchema>;
type _CommandDescriptorZ = z.infer<typeof commandDescriptorSchema>;

const _localisedHelpAssign = (h: LocalizedHelp): _LocalizedHelpZ => h;
const _parameterAssign = (p: ParameterDescriptor): _ParameterDescriptorZ => p;
const _draftStepAssign = (s: DraftStepDescriptor): _DraftStepDescriptorZ => s;
const _descriptorAssign = (d: CommandDescriptor): _CommandDescriptorZ => d;

// Burn the unused-symbol warning — these consts exist only for the
// compile-time assignability side-effect.
void _localisedHelpAssign;
void _parameterAssign;
void _draftStepAssign;
void _descriptorAssign;
